import {
  LookupList,
  Numeric,
  Popover,
  QuickPickRaw,
  ResponsivePanel,
  Text,
  Toggle
} from 'bz-core-controls';
import Lookup from 'bz-core-controls/controls/clinical/clinical-lookup';
import OutlineButton from 'bz-core-controls/controls/general/outline-button';
import { ConfigObservable } from 'bz-stores/lib/config';
import { ETemplateType } from 'bz-stores/mobx/template-store';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React from 'react';
import ReactCSSTransitionReplace from 'react-css-transition-replace';

import Api from 'tm3-core/api';
import { EHeidiConfigKeys } from 'tm3-core/enums/config-enum';

import z from 'z';

import { DataSourceMenu, DataSourceMenuItem } from '../../../../../controls/data-source';
import AddReportingFieldDialog from './add-report-field-dialog';
import AddressAttributes from './address-props';
import AutoFlag from './autoflag-props';
import FormulaEditor from './formula-editor';
import FormulaKeyHelper from './formula-key-helper';
import ScoreEditor from './score-editor';
import TableProps from './table-props';

const aiAutofillSupportedControls = ['Text', 'Lookup', 'YesNo', 'Numeric', 'DatePicker'];

@observer
class ControlAttributes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      menu: 'attributes',
      transition: 'slide-left',
      property: 'src'
    };
  }

  render() {
    let { menu, transition, property } = this.state;
    let { attributes, store } = this.props;
    let control = store.controls[this.props.type] || {};
    let defaults = control.attributes.toJS ? control.attributes.toJS() : control.attributes;

    let advanced = [...new Set(control.advanced)];
    if (
      aiAutofillSupportedControls.includes(this.props.type) &&
      !advanced.includes('excludeFromAiAutofill')
    ) {
      advanced.push('excludeFromAiAutofill');
    }
    let keys = [
      ...new Set(
        Object.keys(attributes)
          .concat(defaults)
          .filter((x) => !advanced.includes(x))
      )
    ];

    // Hack to move 'Reporting Field' attribute to the end of the list
    // as default is alphabetical order.
    if (keys.includes('field')) {
      keys = keys.filter((x) => x !== 'field');
      advanced.push('field');
    }
    const content = {
      attributes: () => {
        let attrs = keys.map((x, i) => {
          return (
            <Attribute
              key={i}
              name={x}
              store={store}
              control={control}
              value={attributes[x]}
              otherAttributes={attributes}
              onNavigate={this.onNavigate.bind(this, 'datasource', 'left')}
              isValid={this.isValid.bind(this, x)}
              onChange={this.onChange.bind(this, x)}
            />
          );
        });
        let advancedAttrs = advanced.map((x, i) => {
          return (
            <Attribute
              key={i}
              name={x}
              store={store}
              control={control}
              value={attributes[x]}
              otherAttributes={attributes}
              onNavigate={this.onNavigate.bind(this, 'datasource', 'left')}
              isValid={this.isValid.bind(this, x)}
              onChange={this.onChange.bind(this, x)}
            />
          );
        });
        return (
          <div>
            {attrs}
            {advanced.length > 0 && (
              <ResponsivePanel title="Advanced">{advancedAttrs}</ResponsivePanel>
            )}
          </div>
        );
      },
      datasource: () => {
        const showLookupsForNonCaseflowTemplates = !['R', 'P', 'N'].includes(store.type);
        const isForm = [
          ETemplateType.Registration,
          ETemplateType.ClinicalForm,
          ETemplateType.Survey
        ].includes(store.type);

        return (
          <DataSourceMenu
            label="Data Source"
            onNavigate={this.onNavigate.bind(this, 'attributes', 'right', property)}
          >
            <DataSourceMenuItem
              label="Custom"
              next={true}
              icon="fa-puzzle-piece"
              onClick={this.onNavigate.bind(this, 'custom', 'left', property)}
            />
            {showLookupsForNonCaseflowTemplates && (
              <DataSourceMenuItem
                label="Lookup"
                next={true}
                icon="fa-info-circle"
                onClick={this.onNavigate.bind(this, 'lookup', 'left', property)}
              />
            )}
            {!isForm && (
              <DataSourceMenuItem
                label="Search"
                next={true}
                icon="fa-search"
                onClick={this.onNavigate.bind(this, 'search', 'left', property)}
              />
            )}
          </DataSourceMenu>
        );
      },
      custom: () => {
        let { lists } = this.props.store;
        let map = {};
        if (lists._idToNameMap) {
          Object.keys(lists._idToNameMap).forEach((x) => (map[lists._idToNameMap[x]] = x));
        }
        let items = Object.keys(lists)
          .filter((name) => name !== '_idToNameMap')
          .map((x, i) => {
            return (
              <DataSourceMenuItem
                key={i}
                label={x}
                onClick={this.onClick.bind(this, map[x] || x, property)}
              />
            );
          });

        return (
          <DataSourceMenu
            label="Custom"
            onNavigate={this.onNavigate.bind(this, 'datasource', 'right', property)}
          >
            {items}
          </DataSourceMenu>
        );
      },
      lookup: () => {
        let { lookups } = this.props.store;
        const { property } = this.state;
        let items = Object.keys(lookups).map((x, i) => {
          let name = lookups[x].niceName || lookups[x].name || x;
          return (
            <DataSourceMenuItem
              key={i}
              label={name}
              onClick={this.onClick.bind(this, x, property)}
            />
          );
        });

        return (
          <DataSourceMenu
            label="Lookup"
            onNavigate={this.onNavigate.bind(this, 'datasource', 'right', property)}
          >
            {items}
          </DataSourceMenu>
        );
      },
      search: () => {
        let { searches } = this.props.store;
        let items = Object.keys(searches).map((x, i) => {
          return (
            <DataSourceMenuItem key={i} label={x} onClick={this.onClick.bind(this, x, property)} />
          );
        });

        return (
          <DataSourceMenu
            label="Search"
            onNavigate={this.onNavigate.bind(this, 'datasource', 'right', property)}
          >
            {items}
          </DataSourceMenu>
        );
      }
    };
    return (
      <ReactCSSTransitionReplace
        transitionName={transition}
        transitionEnterTimeout={400}
        transitionLeaveTimeout={400}
        className={transition}
      >
        <div key={menu}>{content[menu]()}</div>
      </ReactCSSTransitionReplace>
    );
  }

  onClick(value, property = 'src') {
    this.setState({
      menu: 'attributes',
      transition: 'slide-left',
      property
    });
    this.props.onChange(property, value);
  }

  onChange(key, value) {
    this.props.onChange(key, value);
  }

  isValid(key, valid) {
    this.props.isValid(key, valid);
  }

  onNavigate(key, dir, property = 'src') {
    this.setState({
      menu: key,
      transition: `slide-${dir}`,
      property
    });
  }
}

ControlAttributes.defaultProps = {
  attributes: {}
};

@observer
class Attribute extends React.Component {
  render() {
    let { store, name, value } = this.props;
    let { lookups, lists, type } = store;
    let extras = { onChange: this.onChange.bind(this) };
    let ctrl, overrideName;

    switch (name) {
      case 'inline':
      case 'required':
      case 'maitland':
      case 'keyquestion':
      case 'multi':
      case 'list':
      case 'full-lookup-path':
      case 'text-if-yes':
      case 'text-if-no':
      case 'hideonreferralportal':
      case 'hideonpatientportal':
      case 'hiddenfield':
      case 'hideFromForms':
      case 'limitToBusiness':
      case 'disableCheckboxes':
      case 'isEmail':
      case 'restrictFutureDate':
      case 'hideLabel':
      case 'readOnly':
      case 'hideCategory':
      case 'hideBusinessName':
      case 'hideAddress':
      case 'hideProviderNumber':
      case 'hideRelationship':
      case 'hideMobile':
        ctrl = Toggle;
        extras = { checked: value, onChange: this.onToggle.bind(this) };
        if (name === 'maitland') {
          overrideName = 'Show Maitland Symbols';
        }
        if (name === 'inline') {
          overrideName = 'Show Inline';
        }
        if (name === 'keyquestion') {
          overrideName = 'Key Question';
        }
        if (name === 'multi') {
          overrideName = 'Multi-select';
        }
        if (name === 'list') {
          overrideName = 'Display as list';
        }
        if (name === 'full-lookup-path') {
          overrideName = 'Output text from all lookup levels';
        }
        if (name === 'text-if-yes') {
          overrideName = "Require reason if 'Yes'";
        }
        if (name === 'text-if-no') {
          overrideName = "Require reason if 'No'";
        }
        if (name === 'limitToBusiness') {
          overrideName = 'Limit search to group contacts only';
        }
        if (name === 'disableCheckboxes') {
          overrideName = 'Disable contact type checkboxes';
        }
        if (name === 'hideonreferralportal') {
          overrideName = 'Hide control on referral portal';
        }
        if (name === 'hideonpatientportal') {
          overrideName = z.format('Hide control on {patient} portal');
        }
        if (name === 'isEmail') {
          overrideName = 'Is Email Field';
        }
        if (name === 'restrictFutureDate') {
          overrideName = 'Restrict Future Date';
        }
        if (name === 'hiddenfield') {
          overrideName = 'Hidden Field';
        }
        if (name === 'hideFromForms') {
          if (!store.isEdittingFormTemplates) {
            return null;
          }
          overrideName = z.format('Hide control for {patient} submission');
        }
        if (name === 'hideLabel') {
          overrideName = 'Hide Name';
        }
        if (name === 'readOnly') {
          overrideName = 'Read Only';
        }
        // Referral Contact (caseflow-lite) per-section visibility toggles.
        // Kept alongside the other hide* toggles so they render as switches
        // instead of falling through to the default text-input renderer.
        if (name === 'hideCategory') {
          overrideName = 'Hide Category';
        }
        if (name === 'hideBusinessName') {
          overrideName = 'Hide Business Name';
        }
        if (name === 'hideAddress') {
          overrideName = 'Hide Address';
        }
        if (name === 'hideProviderNumber') {
          overrideName = 'Hide Provider Number';
        }
        if (name === 'hideRelationship') {
          overrideName = 'Hide Relationship';
        }
        if (name === 'hideMobile') {
          overrideName = 'Hide Mobile';
        }
        break;

      case 'excludeFromAiAutofill':
        if (!ConfigObservable.isEnabled(EHeidiConfigKeys.IsEnabled)) {
          return null;
        }
        ctrl = Toggle;
        extras = { checked: value === true, onChange: this.onToggle.bind(this) };
        overrideName = 'Exclude from AI Autofill';
        break;

      case 'min':
      case 'rows':
      case 'max':
      case 'decimals':
        ctrl = Numeric;
        if (name === 'min') {
          overrideName = 'Min Length';
        }
        if (name === 'max') {
          overrideName = 'Max Length';
        }
        if (name === 'rows') {
          overrideName = 'Rows';
        } // TODO: better name?
        if (name === 'decimals') {
          overrideName = 'Decimal Places';
        }
        if (name == 'increment') {
          overrideName = 'Increment';
        }
        break;

      case 'other':
        overrideName = 'Allow "Other" option';
        ctrl = Lookup;
        extras = {
          placeholder: 'Select an Option',
          src: ['Not Allowed', 'Allowed', 'Allowed with Explanation'],
          onChange: this.onChange.bind(this),
          style: { width: 190 }
        };
        break;

      case 'size':
        overrideName = 'Size';
        ctrl = Lookup;
        extras = {
          placeholder: 'Select an Option',
          src: ['Auto-Size', 'Small', 'Medium', 'Large'],
          onChange: this.onChange.bind(this),
          style: { width: 190 },
          value: value || 'Auto-Size'
        };
        break;

      case 'textSize':
        overrideName = 'Size';
        ctrl = Lookup;
        extras = {
          placeholder: 'Select an Option',
          src: ['Small', 'Medium', 'Large'],
          onChange: this.onChange.bind(this),
          style: { width: 190 },
          value: value || 'Small'
        };
        break;

      case 'appendMode':
        overrideName = 'Symbol to append quick picks with';
        ctrl = Lookup;
        extras = {
          placeholder: 'Select a separator',
          src: ['Space', 'Comma', 'New Line', 'New Line Dash'],
          onChange: this.onChange.bind(this),
          style: { width: 190 }
        };
        break;

      case 'defaultCategory':
        // Designer-time default for the Referral Contact control's Category
        // field. Bound to the CONTACTCATEGORY tenant lookup so the list stays
        // in sync with what "Add Medical Contact" on the patient profile
        // offers. When the Category picker is hidden at fill time, the
        // control still emits this value so the materialised contact ends up
        // in the right category (e.g. GP).
        overrideName = 'Default Category';
        ctrl = Lookup;
        extras = {
          placeholder: 'Select a category',
          src: 'CONTACTCATEGORY',
          lookups: lookups,
          onChange: this.onChange.bind(this),
          style: { width: 190 }
        };
        break;

      case 'lookuplist':
        ctrl = LookupList;
        overrideName = 'Options List';
        extras = {
          lookups: lookups,
          onChange: this.onChange.bind(this),
          style: { width: 190 }
        };
        break;

      case 'countryList':
      case 'countyList':
      case 'src':
        ctrl = OutlineButton;

        let propertyName = name;
        if (name == 'countryList') {
          overrideName = 'Country List';
        } else if (name == 'countyList') {
          overrideName = 'County List';
        } else {
          overrideName = 'Custom List';
        }
        name = value || 'Select';

        if (lookups[value]) name = lookups[value].niceName;
        if (lists._idToNameMap[value]) name = lists._idToNameMap[value];

        extras = {
          style: { width: 190 },
          label: name || 'Select',
          onChange: this.onChange.bind(this),
          onClick: this.onNavigate.bind(this, propertyName)
        };
        break;

      case 'field':
        //overrideName = control.tag === 'Label' ? 'Label' : 'Reporting Field'
        overrideName = 'Reporting';
        ctrl = FieldAttribute;
        break;

      case 'autoflag':
        overrideName = 'Create Flag';
        ctrl = AutoFlag;

        extras = {
          flagMode: true,
          onChange: this.onChange.bind(this),
          subProps: this.props.otherAttributes
        };
        if (value == null) extras.value = { operator: '=' };
        break;

      case 'tableConfig':
        overrideName = 'Columns';
        const { tableConfig } = this.props.otherAttributes;
        const { title, columns } = tableConfig || {};
        ctrl = TableProps;
        extras = {
          onChange: this.onChange.bind(this),
          title: title,
          columns: columns || [],
          store: store
        };
        break;

      case 'formula':
        overrideName = 'Formula';

        ctrl = FormulaEditor;
        break;

      case 'formulakey':
        overrideName = 'Formula Variable ';
        ctrl = FormulaKeyHelper;
        break;

      case 'scoreGoalLink':
        if (type === ETemplateType.Registration || type === ETemplateType.Survey) {
          return null;
        }

        overrideName = 'Link To  ';
        ctrl = ScoreEditor;
        extras = {
          placeholder: 'Select an Option',
          src: ['Not Allowed', 'Allowed', 'Allowed with Explanation'],
          onChange: this.onChange.bind(this),
          style: { width: 190 }
        };
        break;

      case 'addressConfig':
        overrideName = 'Required Fields';
        ctrl = AddressAttributes;
        break;

      case 'ispsfs':
        return (
          <div>
            This component will render the activities set up in the clinical case. A goal for each
            activity will be created as well as an average score.
          </div>
        );
        break;

      default:
        if (name === 'label') {
          overrideName = 'Name';
          let { required, label } = this.props.otherAttributes;
          let errorText =
            required === true && (label || '').trim() === ''
              ? 'Please enter a name - this is needed because this field is required.'
              : '';
          extras = { errorText: errorText, style: { marginBottom: '20px !important' } };
        }
        if (name === 'placeholder') {
          overrideName = 'Hint Text';
        }
        ctrl = Text;
    }

    const fullWidth = ['tableConfig'].includes(name);
    name = overrideName ? overrideName : name.replace(/^[a-z]/, name.substring(0, 1).toUpperCase());
    return (
      <div className={!fullWidth ? 'attribute' : ''}>
        <div className="attribute-contents">
          <div className="attribute-title">{name}</div>
          <div className="attribute-body">
            {React.createElement(ctrl, Object.assign({}, this.props, extras))}
          </div>
        </div>
      </div>
    );
  }

  onToggle(e, value) {
    this.props.onChange(value);
  }

  onChange(value) {
    this.props.onChange(value);
  }

  onNavigate(property = 'src') {
    this.props.onNavigate(property);
  }
}

Attribute.defaultProps = {
  name: '',
  value: '',
  onNavigate: () => {}
};

@inject('templateEditorStore')
@observer
class FieldAttribute extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      open: false,
      selected: 'main',
      showDialog: false
    };
  }
  target: any;

  render() {
    let { selected, showDialog } = this.state;
    let { store, value, control } = this.props;

    let reportingFields =
      control.tag !== 'Label'
        ? store.reportingFields.filter((x) => x.key !== 'X' && x.key !== 'P')
        : store.reportingFields;

    let schema = [];
    let menu = { main: {} };

    reportingFields.map((x) => {
      let fields = control.type !== 'any' ? _.filter(x.fields, { type: control.type }) : x.fields;

      let item = { key: x.key, name: x.name, fields: fields };
      if (fields.length > 0) {
        schema.push(item);
      }
    });

    if (schema.length > 0) {
      schema.map((x) => {
        menu.main[`main.${x.key}`] = x.name;
      });
      schema.map((x) => {
        let key = `main.${x.key}`;
        menu[key] = {};
        x.fields.map((f) => {
          menu[`${key}`][`${f.field}`] = f.name;
        });
      });
    }
    let displayText = '';
    if (value == 'main') {
      displayText = 'None Selected';
    } else {
      let index = value.indexOf('.');
      let section = value.substring(0, index);
      let subMenu = menu['main.' + section];
      if (subMenu) displayText = subMenu[value];
      else displayText = value;
    }
    return (
      <div>
        <OutlineButton className="w-[200px]" onClick={this.onClick.bind(this)}>
          {displayText}
        </OutlineButton>
        {store.type === 'N' && (
          <a onClick={() => this.setState({ showDialog: true })}>Add new reporting field</a>
        )}
        <Popover
          {...this.state}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          targetOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          style={{
            position: 'absolute',
            bottom: '10px'
          }}
          onRequestClose={this.onRequestClose.bind(this)}
        >
          <QuickPickRaw
            menu={menu}
            selected={selected}
            onChange={this.onChange.bind(this)}
            onNavigate={this.onNavigate.bind(this)}
          />
        </Popover>
        {showDialog && (
          <AddReportingFieldDialog
            onRequestClose={() => {
              this.setState({ showDialog: false });
              let that = this;
              Api.get('/api/clinical/settings/reportingfields').then((res) => {
                that.props.templateEditorStore.setReportingFields(res);
              });
            }}
          />
        )}
      </div>
    );
  }

  onClick(e) {
    this.target = e.target;
    this.setState({
      open: true,
      anchorEl: e.target
    });
  }
  onNavigate(key) {
    this.setState({ selected: key, anchorEl: this.target });
  }

  onChange(key, name) {
    this.props.onChange(key);
    this.setState({ open: false });
  }

  onRequestClose() {
    this.setState({ open: false });
  }
}

export default ControlAttributes;

export const { defaultProps } = ControlAttributes;
